/**
 * All possible agent project status values.
 * This should be kept in sync with backend/app/services/analytics_service.py AGENT_PROJECT_STATUSES
 */
export const ALL_AGENT_PROJECT_STATUSES = [
  'DISCOVERY',
  'DESIGN',
  'TECHNICAL_ARCHITECTURE',
  'IMPLEMENTATION_PLANNING',
  'CODE_GENERATION',
  'OPERATIONS_PLANNING',
  'COMPLETE',
] as const;

/**
 * Type for agent project status values
 */
export type AgentProjectStatus = (typeof ALL_AGENT_PROJECT_STATUSES)[number];

/**
 * Statuses that indicate a completed project.
 * Handles legacy values (PRD_APPROVED, completed) for backward compatibility.
 */
export const COMPLETED_STATUSES = [
  'COMPLETE',
  'PRD_APPROVED',
  'completed',
] as const;

/**
 * Statuses that indicate a project is under review (later stages).
 */
export const REVIEW_STATUSES = [
  'OPERATIONS_PLANNING',
  'CODE_GENERATION',
  'operations',
  'codegen',
] as const;

/**
 * Check if a status represents a completed project.
 * Handles case-insensitive comparison and legacy values.
 */
export function isCompletedStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  const normalizedStatus = status.toUpperCase();
  return COMPLETED_STATUSES.map((s) => s.toUpperCase()).includes(
    normalizedStatus
  );
}

/**
 * Check if a status represents a project under review.
 * Handles case-insensitive comparison.
 */
export function isReviewStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  const normalizedStatus = status.toLowerCase();
  return REVIEW_STATUSES.map((s) => s.toLowerCase()).includes(normalizedStatus);
}

/**
 * Check if a status represents an in-progress project (not completed).
 */
export function isInProgressStatus(status: string | null | undefined): boolean {
  return !!status && !isCompletedStatus(status);
}

/**
 * Format a status for display (title case, replace underscores with spaces).
 */
export function formatStatusForDisplay(
  status: string | null | undefined
): string {
  if (!status) return 'Unknown';
  return status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * @deprecated Use AgentProjectStatus instead
 */
export type BusinessCaseStatus = AgentProjectStatus;

/**
 * @deprecated Use ALL_AGENT_PROJECT_STATUSES instead
 */
export const ALL_BUSINESS_CASE_STATUSES = ALL_AGENT_PROJECT_STATUSES;
